import { useMemo, useState } from 'react'
import type { Role } from '@shared/types'
import type { Roll } from '@shared/types'
import { ApiError, api } from '@/lib/api'
import { Button, EmptyState, ErrorNote, Panel, SectionHeading, Select } from '@/components/ui'
import { RollResultCard } from './RollResultCard'
import { cn } from '@/lib/cn'

type Filter = 'all' | 'crits' | 'd20'

export interface RollFeedProps {
  campaignId: string
  myRole: Role
  /** Owned by the page, so the dice roller can drop results straight in. */
  rolls: Roll[]
  loading: boolean
  onCleared: () => void
}

export function RollFeed({ campaignId, myRole, rolls, loading, onCleared }: RollFeedProps) {
  const [who, setWho] = useState('all')
  const [filter, setFilter] = useState<Filter>('all')
  const [error, setError] = useState<string | null>(null)
  const isDm = myRole === 'dm'

  const rollers = useMemo(() => {
    const names = new Map<string, string>()
    for (const roll of rolls) {
      if (roll.userId) names.set(roll.userId, roll.userName ?? 'Someone')
    }
    return [...names.entries()]
  }, [rolls])

  const visible = rolls.filter((roll) => {
    if (who !== 'all' && roll.userId !== who) return false
    if (filter === 'crits') return roll.outcome !== 'normal'
    if (filter === 'd20') return roll.dice.some((die) => die.sides === 20)
    return true
  })

  // Group by day so a long feed stays readable between sessions.
  const groups = visible.reduce<{ label: string; rolls: typeof visible }[]>((acc, roll) => {
    const label = new Date(roll.createdAt).toLocaleDateString(undefined, {
      weekday: 'long', month: 'long', day: 'numeric',
    })
    const last = acc[acc.length - 1]
    if (last && last.label === label) last.rolls.push(roll)
    else acc.push({ label, rolls: [roll] })
    return acc
  }, [])

  return (
    <div className="space-y-5">
      <SectionHeading
        action={
          <div className="flex flex-wrap items-center gap-2">
            <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-ink-500">
              <span aria-hidden className="size-1.5 animate-pulse rounded-full bg-moss-500" />
              live
            </span>
            {rollers.length > 1 && (
              <Select value={who} onChange={(e) => setWho(e.target.value)} aria-label="Filter by who rolled"
                className="h-8 w-32 py-0 text-xs">
                <option value="all">Everyone</option>
                {rollers.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
              </Select>
            )}
            <div className="flex gap-1 rounded-lg bg-ink-900/70 p-0.5">
              {([['all', 'All'], ['d20', 'd20s'], ['crits', 'Crits']] as const).map(([value, label]) => (
                <button key={value} type="button" onClick={() => setFilter(value)}
                  className={cn('rounded-md px-2 py-1 text-[12px] transition-colors',
                    filter === value ? 'bg-ink-750 text-gold-400' : 'text-ink-400 hover:text-ink-200')}>
                  {label}
                </button>
              ))}
            </div>
            {isDm && rolls.length > 0 && (
              <Button size="sm" variant="ghost" onClick={async () => {
                setError(null)
                try { await api.clearRolls(campaignId); onCleared() }
                catch (err) { setError(err instanceof ApiError ? err.message : 'Could not clear the feed') }
              }}>
                Clear
              </Button>
            )}
          </div>
        }
      >
        Dice
      </SectionHeading>

      {error && <ErrorNote>{error}</ErrorNote>}

      {loading ? (
        <Panel className="p-6 text-center text-sm text-ink-500">Loading…</Panel>
      ) : rolls.length === 0 ? (
        <EmptyState
          title="No dice yet"
          description={isDm
            ? "Every roll anyone makes turns up here within a few seconds, so you can watch the table without leaning over anyone's shoulder."
            : 'Rolls you and the rest of the party make show up here.'}
        />
      ) : visible.length === 0 ? (
        <Panel className="p-6 text-center text-sm text-ink-500">Nothing matches that filter.</Panel>
      ) : (
        <div className="space-y-5">
          {groups.map((group) => (
            <section key={group.label}>
              <h3 className="mb-2 text-[11px] uppercase tracking-[0.18em] text-ink-500">{group.label}</h3>
              <div className="space-y-2">
                {group.rolls.map((roll) => <RollResultCard key={roll.id} roll={roll} />)}
              </div>
            </section>
          ))}
        </div>
      )}

      <span className="sr-only" aria-live="polite">
        {visible[0] ? `${visible[0].userName ?? 'Someone'} rolled ${visible[0].total}` : ''}
      </span>
    </div>
  )
}
